/**
 * Producing the export files on the device (Epic 11).
 *
 * All of it runs locally against the replica, which is the point: FR-11.4 says
 * an export needs no operator, and the amended NFR-FLEX-1 budgets 15 minutes for
 * the records. A path with no server in it cannot miss either.
 *
 * Only the photo *URLs* need the network, because only S3 can sign them.
 */
import {
  downloaderScript,
  exportDocument,
  itemsCsv,
  photoExportRows,
  photoManifestCsv,
  type AnyEvent,
  type ExportedItem,
  type Manifest,
  type PhotoExportRow,
} from '@intentionalist/shared';
import { downloadUrls } from './photos.js';

export interface ExportFile {
  readonly name: string;
  readonly blob: Blob;
}

const stamp = (at: Date): string => at.toISOString().slice(0, 10);

const json = (value: unknown): Blob =>
  new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' });

const text = (value: string, type = 'text/plain'): Blob => new Blob([value], { type });

/** FR-11.1. `withHistory` adds the event log, which is most of the bytes. */
export function catalogJson(
  manifest: Manifest,
  options: { events?: readonly AnyEvent[]; at?: Date } = {},
): ExportFile {
  const at = options.at ?? new Date();
  return {
    name: `intentionalist-${stamp(at)}.json`,
    blob: json(
      exportDocument(manifest, {
        ...(options.events === undefined ? {} : { events: options.events }),
        exportedAt: at.toISOString(),
      }),
    ),
  };
}

/**
 * FR-11.2, over whichever items the caller chose.
 *
 * The subset is decided by the caller because the caller is the screen that has
 * the filter. Re-deriving it here is how an export and the list it came from
 * come to disagree.
 */
export function catalogCsv(items: readonly ExportedItem[], at = new Date()): ExportFile {
  return {
    name: `intentionalist-${stamp(at)}.csv`,
    // A byte-order mark, which NFR-COMP-5 needs: Excel assumes the system code
    // page for a .csv and mis-decodes UTF-8 without one, so an item called
    // "Dad's chaise longue" opens as mojibake. Numbers and Sheets detect UTF-8
    // either way and skip the mark, so it costs them nothing.
    //
    // Added at the file boundary rather than inside `itemsCsv`, because the mark
    // is a property of the file handed to a spreadsheet, not of the format: a
    // caller parsing the string back would have to strip it again.
    blob: text(`\ufeff${itemsCsv(items)}`, 'text/csv;charset=utf-8'),
  };
}

export interface PhotoExport {
  readonly files: ExportFile[];
  readonly requested: number;
  /** Keys the server would not sign, which a caller should report rather than hide. */
  readonly unsigned: PhotoExportRow[];
  readonly totalBytes: number;
}

/**
 * FR-11.3: the photo originals, as signed URLs plus something that fetches them.
 *
 * No archive is assembled. The amended NFR-FLEX-1 is explicit about why -- at
 * the reference workload the originals are around 25 GB, which a 15-minute
 * Lambda with 10 GB of scratch cannot zip, and always-on compute to do it is
 * forbidden by NFR-CON-3. What the user gets instead is every URL and a script,
 * so the bytes move at the speed of their own connection.
 */
export async function photoExport(
  manifest: Manifest,
  expiresInSeconds = 3600,
  at = new Date(),
): Promise<PhotoExport> {
  const rows = photoExportRows(manifest);
  const urls = await downloadUrls(rows.map((row) => row.key));

  const signed: (PhotoExportRow & { url: string })[] = [];
  const unsigned: PhotoExportRow[] = [];
  for (const row of rows) {
    const url = urls[row.key];
    if (url === undefined) unsigned.push(row);
    else signed.push({ ...row, url });
  }

  const totalBytes = photoBytes(manifest);

  return {
    requested: rows.length,
    unsigned,
    totalBytes,
    files:
      signed.length === 0
        ? []
        : [
            {
              name: `intentionalist-photos-${stamp(at)}.csv`,
              blob: text(photoManifestCsv(signed), 'text/csv'),
            },
            {
              name: `intentionalist-photos-${stamp(at)}.sh`,
              // Shell, not a zip: see above. The script is what makes a list of
              // several hundred URLs usable by a person.
              blob: text(downloaderScript(signed, expiresInSeconds), 'application/x-sh'),
            },
          ],
  };
}

/** Separate so the UI can state the size before anyone starts a 25 GB download. */
export function photoBytes(manifest: Manifest): number {
  let total = 0;
  for (const item of manifest.items.values()) {
    for (const photo of item.photos.values()) {
      if (photo.uploaded?.v !== true) continue;
      total += photo.metadata?.v.byteSize ?? 0;
    }
  }
  return total;
}

/**
 * Hands a file to the browser.
 *
 * An object URL and a synthetic click, which is the only portable way to save a
 * generated file without a server to download it from. The URL is revoked on the
 * next frame rather than immediately: Safari has not started reading it when
 * `click()` returns.
 */
export function saveFile(file: ExportFile): void {
  const url = URL.createObjectURL(file.blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = file.name;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  requestAnimationFrame(() => URL.revokeObjectURL(url));
}

/** Rough, and labeled as such wherever it is shown. */
export function humanBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}
